import { IValueInputAttachable } from "client/Forms/attachable";
import { ReactElement, useMemo } from "react";
import { HubTileDefaultViewConfigurationFormModel } from "./EditHubTile";
import { HubItemCategoryIdentifierModel } from "client/Api/Hub/common";
import { HubTileDefaultViewKind } from "client/Api/Workspace/workspace";
import { never } from "client_legacy/ndaw/errors";
import {
	FormItem,
	MultiSelect,
	MultiSelectItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { ensureNotNil, isNotNil } from "client/Misc/utils";

export type HubItemDefaultViewSelectorProps = {
	attachable: IValueInputAttachable<HubTileDefaultViewConfigurationFormModel>;
	categories: readonly HubItemCategoryIdentifierModel[];
};

export const HubItemDefaultViewSelector = (props: HubItemDefaultViewSelectorProps): ReactElement => {
	const {
		attachable,
		categories
	} = props;

	const {
		value
	} = attachable;

	const defaultView = useMemo(
		(): DefaultCategoryValue => {
			switch (value.kind) {
				case HubTileDefaultViewKind.AllItems:
					return null;
				case HubTileDefaultViewKind.Category:
					return value.categoryId;
				case HubTileDefaultViewKind.CategoryCollection:
					return "multiCategories";
				default:
					never(value);
			}
		},
		[value]
	);

	const defaultViewChangeHandler = (defaultView: DefaultCategoryValue): void => {
		let newValue: HubTileDefaultViewConfigurationFormModel;
		switch (defaultView) {
			case "multiCategories":
				newValue = {
					...value,
					kind: HubTileDefaultViewKind.CategoryCollection
				};
				break;
			case null:
				newValue = {
					...value,
					kind: HubTileDefaultViewKind.AllItems
				};
				break;
			default:
				newValue = {
					...value,
					kind: HubTileDefaultViewKind.Category,
					categoryId: defaultView
				};
				break;
		}

		attachable.reducer
			.setValue(newValue);

		attachable.validation
			.triggerInputFinished();
	};

	const defaultViewOptions = useMemo(
		(): readonly SelectItem<DefaultCategoryValue>[] => {
			const result: readonly SelectItem<DefaultCategoryValue>[] = categories
				.map(
					(category) => {
						return {
							value: category.categoryId,
							text: category.name
						};
					}
				);

			return [
				{
					text: localize("_Custom"),
					value: "multiCategories",
					groupName: "multiCategories"
				},
				...result
			];
		},
		[categories]
	);

	const customViewOptions = useMemo(
		(): readonly MultiSelectItem<number>[] => {
			const result = categories
				.filter(x => isNotNil(x.categoryId))
				.map(
					(category) => ({
						value: ensureNotNil(category.categoryId),
						text: category.name,
					})
				);

			return result;
		},
		[categories]
	);

	return (
		<>
			<FormItem label={localize("_DefaultView")}>
				<Select
					options={defaultViewOptions}
					value={defaultView}
					onChange={defaultViewChangeHandler}
					menuTitle={localize("_DefaultView")}
					trackingId="default-view-select"
				/>

				<ValidationError
					{...attachable.for("categoryId")
						.attachToValidationError()
					}
				/>
			</FormItem>
			{attachable
				.when(x => x.kind === HubTileDefaultViewKind.CategoryCollection,
					categoryCollectionAttachable => (
						<FormItem label={localize("_CategoriesInCustomView")}>
							<MultiSelect
								{...categoryCollectionAttachable.for("categoryIds").attachToMultiSelect()}
								options={customViewOptions}
								menuTitle={localize("_CategoriesInCustomView")}
								placeholder={localize("_SelectCategories")}
							/>
							<ValidationError {...categoryCollectionAttachable.for("categoryIds").attachToValidationError()} />
						</FormItem>
					)
				)
			}
		</>
	);
};
HubItemDefaultViewSelector.displayName = "HubItemDefaultViewSelector";

type DefaultCategoryValue = null | number | "multiCategories";
